import {
	ChangeDetectionStrategy,
	Component,
	HostListener,
	OnInit,
} from '@angular/core';
import { User } from '@angular/fire/auth';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { BaseComponent } from '@core/base.component';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { NavigationService } from '@services/nav/nav.service';
import {
	RemoteConfigFlags,
	RemoteConfigService,
} from '@services/remoteConfig/remote-config.service';
import { AuthDispatch } from '@state/auth/auth.dispatch';
import { UserProfileState } from '@state/user-profile';
import LogRocket from 'logrocket';

type CurrentStep = {
	stepName: EmailVerificationSteps;
	nextAction?: VoidFunction;
	backAction?: VoidFunction;
	nextActionText?: string;
	backActionText?: string;
	headingText?: string;
	subText?: string;
	paragraphText?: string;
	imageUrl?: string;
	alternativeText?: string;
};

enum EmailVerificationSteps {
	EMAIL_SENT = 0,
	EMAIL_RESENT = 1,
	ACCOUNT_VERIFIED = 2,
	VERIFY_ACCOUNT = 3,
}

type ICurrentStep = Record<EmailVerificationSteps, CurrentStep>;

interface VM {
	currentStep: CurrentStep;
}

@Component({
	selector: 'app-verify-email',
	templateUrl: './verify-email.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class VerifyEmailComponent extends BaseComponent<VM> implements OnInit {
	public stepCount = 0;
	public emailSentCount = 1; // already sent by the time the component is initialized
	public playAnimation = false;
	private user: User;
	public emailVerificationGracePeriodEndDate: string;

	@SelectSnapshot(UserProfileState.getCurrentBusinessId) businessId: string;

	constructor(
		private auth: AngularFireAuth,
		private authDispatch: AuthDispatch,
		private route: NavigationService,
		private remoteConfigService: RemoteConfigService,
	) {
		super({
			currentStep: null,
		});
	}

	AllSteps: ICurrentStep;

	@HostListener('window:focus')
	async onWindowFocus(): Promise<void> {
		await this.user?.reload();
		if (this.user?.emailVerified) {
			this.VM = {
				currentStep: this.AllSteps[EmailVerificationSteps.ACCOUNT_VERIFIED],
			};
		}
	}

	async ngOnInit(): Promise<void> {
		await this.setUser();
		this.emailVerificationGracePeriodEndDate =
			await this.remoteConfigService.getValueByKey(
				RemoteConfigFlags.EMAIL_VERIFICATION_GRACE_PERIOD_END_DATE,
			);

		this.AllSteps = {
			[EmailVerificationSteps.EMAIL_SENT]: {
				stepName: EmailVerificationSteps.EMAIL_SENT,
				nextAction: () => this.resendEmail(),
				nextActionText: 'Resend Verification Email',
				backAction: () =>
					this.businessId ? this.goToDashboard() : this.goToLogin(),
				backActionText: this.businessId ? 'Go to Dashboard' : '< Back to Login',
				headingText: "You've Got Mail",
				subText: `An email has been sent to ${this.user.email}`,
				paragraphText:
					'Check your inbox and select the link provided in the email to verify your account.',
				imageUrl: '/assets/images/auth/undraw-happy-news.webp',
				alternativeText:
					'Woman holding up a letter with one arm raised in the air.',
			},
			[EmailVerificationSteps.EMAIL_RESENT]: {
				stepName: EmailVerificationSteps.EMAIL_RESENT,
				nextAction: () => this.resendEmail(),
				nextActionText: 'Resend Verification Email',
				backAction: () =>
					this.businessId ? this.goToDashboard() : this.goToLogin(),
				backActionText: this.businessId ? 'Go to Dashboard' : '< Back to Login',
				headingText: 'Another Email Has Been Sent',
				subText: `An email has been sent to ${this.user.email}`,
				paragraphText:
					'Check your inbox and select the link provided in the email to verify your account.',
				imageUrl: '/assets/images/auth/circle-check-filled.webp',
				alternativeText: 'A checkmark inside a circle.',
			},
			[EmailVerificationSteps.ACCOUNT_VERIFIED]: {
				stepName: EmailVerificationSteps.ACCOUNT_VERIFIED,
				nextAction: () => this.dispatchUser(),
				nextActionText: 'Continue',
				headingText: 'Success, Your Account Is Verified!',
				imageUrl: '/assets/images/auth/undraw-celebration.webp',
				alternativeText: 'Two people celebrating with balloons.',
			},
			[EmailVerificationSteps.VERIFY_ACCOUNT]: {
				stepName: EmailVerificationSteps.VERIFY_ACCOUNT,
				nextAction: () => this.goToEmailSent(),
				nextActionText: 'Validate my Account',
				backAction: () => this.goToDashboard(),
				backActionText: 'Go to Dashboard',
				headingText: 'Secure Your Account, Verify Now.',
				subText: `As of ${this.emailVerificationGracePeriodEndDate} email validation will be mandatory for all Closer accounts.`,
				paragraphText: 'Get ahead of the game and validate your account today',
				imageUrl: '/assets/images/auth/undraw-secure-login.webp',
				alternativeText:
					'A person next to life sized laptop screen with padlock logo on it.',
			},
		};

		if (this.businessId) {
			this.VM = {
				currentStep: this.AllSteps[EmailVerificationSteps.VERIFY_ACCOUNT],
			};
		} else {
			this.VM = {
				currentStep:
					this.AllSteps[
						this.user.emailVerified
							? EmailVerificationSteps.ACCOUNT_VERIFIED
							: EmailVerificationSteps.EMAIL_SENT
					],
			};
		}
	}
	goToEmailSent(): void {
		this.VM = { currentStep: this.AllSteps[EmailVerificationSteps.EMAIL_SENT] };
		this.authDispatch.ResendEmailVerification();
	}

	goToDashboard(): void {
		this.route.goToDashboard();
	}

	resendEmail(): void {
		this.emailSentCount++;
		this.playAnimation = this.emailSentCount > 1 && !this.playAnimation;
		this.VM = {
			currentStep: this.AllSteps[EmailVerificationSteps.EMAIL_RESENT],
		};
		this.authDispatch.ResendEmailVerification();
	}

	goToLogin(): void {
		this.authDispatch.LogOut();
	}

	skipVerification(): void {
		throw new Error('Method not implemented.');
	}

	dispatchUser(): void {
		this.authDispatch.SetUser(this.user);
	}

	private async setUser(): Promise<void> {
		try {
			this.user = await this.auth.currentUser;
		} catch (error) {
			LogRocket.error(
				'Failed to get user information for Email Verification',
				error,
			);
			this.goToLogin();
		}
	}
}
